import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert.js';
import salaryService from '../../services/SalaryService.js';
import { Wallet, Eye } from 'lucide-react';
import { FormLayout, FormField, Button } from '../common/index.js';

const EditSalary = () => {
  const { salaryId } = useParams();
  const [formData, setFormData] = useState({
    basic_salary: '',
    allowances: '',
    deductions: '',
    bonus: '',
    overtime_pay: '',
    payment_mode: '',
    transaction_id: '',
    remarks: '',
    status: ''
  });
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [existingScreenshot, setExistingScreenshot] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [netSalary, setNetSalary] = useState(0);
  const [salaryInfo, setSalaryInfo] = useState(null);
  const navigate = useNavigate();

  const statuses = ['pending', 'processing', 'credited', 'hold', 'cancelled'];
  const paymentModes = ['upi', 'cash', 'bank_transfer', 'cheque', 'online'];

  useEffect(() => {
    fetchSalary();
  }, [salaryId]);

  useEffect(() => {
    calculateNetSalary();
  }, [formData.basic_salary, formData.allowances, formData.deductions, formData.bonus, formData.overtime_pay]);

  const fetchSalary = async () => {
    try {
      const response = await salaryService.getById(salaryId);
      const salary = response.data;
      
      setSalaryInfo({
        salary_id: salary.salary_id,
        user_name: salary.user_details?.name,
        user_email: salary.user_details?.email,
        month: salary.month_display,
        year: salary.year
      });
      
      setFormData({
        basic_salary: salary.basic_salary.toString(),
        allowances: salary.allowances.toString(),
        deductions: salary.deductions.toString(),
        bonus: salary.bonus.toString(),
        overtime_pay: salary.overtime_pay.toString(),
        payment_mode: salary.payment_mode || '',
        transaction_id: salary.transaction_id || '',
        remarks: salary.remarks || '',
        status: salary.status
      });
      
      setExistingScreenshot(salary.has_screenshot);
    } catch (err) {
      Alert.error('Failed to load salary data');
      console.error(err);
      navigate('/salaries');
    } finally {
      setLoading(false);
    }
  };

  const calculateNetSalary = () => {
    const basic = parseFloat(formData.basic_salary) || 0;
    const allowances = parseFloat(formData.allowances) || 0;
    const deductions = parseFloat(formData.deductions) || 0;
    const bonus = parseFloat(formData.bonus) || 0;
    const overtime = parseFloat(formData.overtime_pay) || 0;
    
    const net = basic + allowances + bonus + overtime - deductions;
    setNetSalary(net);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('Payment screenshot must be less than 5MB');
        return;
      }
      setPaymentScreenshot(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeScreenshot = () => {
    setPaymentScreenshot(null);
    setScreenshotPreview(null);
    const fileInput = document.getElementById('screenshot-input');
    if (fileInput) fileInput.value = '';
  };

  const viewExistingScreenshot = () => {
    const screenshotUrl = salaryService.getPaymentScreenshot(salaryId);
    window.open(screenshotUrl, '_blank');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await salaryService.update(salaryId, formData, paymentScreenshot);
      Alert.success('Salary record updated successfully!');
      setTimeout(() => navigate('/salaries'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update salary record');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <FormLayout title="Edit Salary Record" icon={Wallet} maxWidth="max-w-7xl">
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-sm text-gray-600">Loading salary data...</p>
          </div>
        </div>
      </FormLayout>
    );
  }

  return (
    <FormLayout
      title="Edit Salary Record"
      icon={Wallet}
      onSubmit={handleSubmit}
      submitLabel={submitting ? 'Updating...' : 'Update Salary Record'}
      loading={submitting}
      onCancel={() => navigate('/salaries')}
      maxWidth="max-w-7xl"
    >
      <div>
        <h3 className="text-base font-semibold text-tertiary mb-4 pb-2 border-b border-gray-100">
          Record Information
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
          <FormField label="Salary ID">
            <p className="text-sm font-semibold text-secondary">{salaryInfo?.salary_id}</p>
          </FormField>
          <FormField label="Employee">
            <p className="text-sm font-semibold text-secondary">{salaryInfo?.user_name}</p>
          </FormField>
          <FormField label="Period">
            <p className="text-sm font-semibold text-secondary">
              {salaryInfo?.month} {salaryInfo?.year}
            </p>
          </FormField>
          <FormField label="Status">
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="input-clean"
            >
                          {statuses.map((status) => (
                            <option key={status} value={status}>
                              {status.toUpperCase()}
                            </option>
                          ))}
            </select>
          </FormField>
        </div>
      </div>

                  {/* Section 2: Salary Components */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Salary Components
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Basic Salary <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="basic_salary"
                          value={formData.basic_salary}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Allowances
                        </label>
                        <input
                          type="text"
                          name="allowances"
                          value={formData.allowances}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Deductions
                        </label>
                        <input
                          type="text"
                          name="deductions"
                          value={formData.deductions}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Bonus
                        </label>
                        <input
                          type="text"
                          name="bonus"
                          value={formData.bonus}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Overtime Pay
                        </label>
                        <input
                          type="text"
                          name="overtime_pay"
                          value={formData.overtime_pay}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Net Salary (Calculated)
                        </label>
                        <div className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm bg-gray-50 text-sm sm:text-base font-semibold text-green-600">
                          {formatCurrency(netSalary)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Payment Details */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Payment Details
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Payment Mode
                        </label>
                        <select
                          name="payment_mode"
                          value={formData.payment_mode}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        >
                          <option value="">Select Payment Mode</option>
                          {paymentModes.map((mode) => (
                            <option key={mode} value={mode}>
                              {mode.replace(/_/g, ' ').toUpperCase()}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Transaction ID
                        </label>
                        <input
                          type="text"
                          name="transaction_id"
                          value={formData.transaction_id}
                          onChange={handleChange}
                          placeholder="Enter transaction ID"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Payment Screenshot
                        </label>
                        
                        {existingScreenshot && !screenshotPreview && (
                          <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            icon={Eye}
                            onClick={viewExistingScreenshot}
                            className="mb-3"
                          >
                            View Existing Screenshot
                          </Button>
                        )}
                        
                        <input
                          id="screenshot-input"
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={handleScreenshotChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                        <p className="text-xs text-gray-600 mt-1">
                          {existingScreenshot ? 'Upload new screenshot to replace existing one (Max 5MB)' : 'Upload payment screenshot (Max 5MB)'}
                        </p>
                        
                        {screenshotPreview && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700 mb-2">New Screenshot Preview:</p>
                            <div className="relative inline-block">
                              <img 
                                src={screenshotPreview} 
                                alt="Screenshot Preview" 
                                className="w-32 h-32 sm:w-40 sm:h-40 object-cover border border-gray-300 rounded-md"
                              />
                              <button
                                type="button"
                                onClick={removeScreenshot}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                                title="Remove screenshot"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Remarks */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Remarks
                    </h3>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Additional Notes
                      </label>
                      <textarea
                        name="remarks"
                        value={formData.remarks}
                        onChange={handleChange}
                        placeholder="Enter any additional notes or remarks"
                        rows="4"
                        className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                      />
                    </div>
                  </div>
    </FormLayout>
  );
};

export default EditSalary;